
import React, { useState, useRef, useEffect } from 'react';
import { Player, Team } from '../types';
import { X, Save, Camera, Ruler, ChevronDown, ChevronRight, Target, Trash2, Loader2, User, AlertCircle, Info } from 'lucide-react';
import { supabase, mapPlayerFromDB } from '../services/supabase';
import {
  UnitSystem,
  parseHeightToCm,
  parseWeightToKg,
  heightCmToImperialFields,
  weightKgToImperialField,
  weightKgToMetricField,
} from '../lib/measurements';

interface PlayerEditModalProps {
  player: Player;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedPlayer: Player) => void;
  onDelete?: (playerId: string) => void;
  organizationId: string;
  isCoach?: boolean;
  teams?: Team[];
  unitSystem?: UnitSystem;
}

const POSITIONS = ['Setter', 'Outside Hitter', 'Opposite', 'Middle Blocker', 'Libero', 'DS', 'Unknown'];

const PREVIEW_SIZE = 192; // px — diameter of the crop circle

const PlayerEditModal: React.FC<PlayerEditModalProps> = ({ player, isOpen, onClose, onUpdate, onDelete, organizationId, isCoach = false, teams = [], unitSystem = 'imperial' as UnitSystem }) => {
  const [formData, setFormData] = useState<Partial<Player>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Height fields — split for imperial, single for metric
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [heightCmInput, setHeightCmInput] = useState('');
  // Weight field
  const [weightInput, setWeightInput] = useState('');
  const [schemaWarning, setSchemaWarning] = useState(false);
  // Dedicated preview state — completely isolated from formData/player sync
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    notes: false
  });

  // Crop / position state — only active when photoPreview is set (new upload)
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const naturalDimsRef = useRef<{ w: number; h: number } | null>(null);
  const cropImgRef = useRef<HTMLImageElement>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingPhotoFile = useRef<File | null>(null);

  useEffect(() => {
    if (player) {
      setFormData({ ...player });
      setPhotoPreview(null); // reset preview when a different player opens
      setCropOffset({ x: 0, y: 0 });
      setCropZoom(1);
      naturalDimsRef.current = null;
      // Populate height fields from canonical cm value
      if (unitSystem === 'imperial') {
        const { feet, inches } = heightCmToImperialFields(player.heightCm);
        setHeightFt(feet);
        setHeightIn(inches);
      } else {
        setHeightCmInput(player.heightCm != null ? String(player.heightCm) : '');
      }
      // Populate weight field
      setWeightInput(
        unitSystem === 'imperial'
          ? weightKgToImperialField(player.weightKg)
          : weightKgToMetricField(player.weightKg)
      );
    }
  }, [player.id, unitSystem]);

  if (!isOpen) return null;

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Clamp offset so the image never exposes the circle background
  const clampOffset = (ox: number, oy: number, zoom: number) => {
    const dims = naturalDimsRef.current;
    if (!dims) return { x: ox, y: oy };
    const coverScale = Math.max(PREVIEW_SIZE / dims.w, PREVIEW_SIZE / dims.h);
    const dw = dims.w * coverScale * zoom;
    const dh = dims.h * coverScale * zoom;
    const maxX = Math.max(0, dw / 2 - PREVIEW_SIZE / 2);
    const maxY = Math.max(0, dh / 2 - PREVIEW_SIZE / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, ox)),
      y: Math.max(-maxY, Math.min(maxY, oy)),
    };
  };

  // Pointer drag handlers
  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY, ox: cropOffset.x, oy: cropOffset.y });
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging || !dragStart) return;
    const rawOx = dragStart.ox + (e.clientX - dragStart.x);
    const rawOy = dragStart.oy + (e.clientY - dragStart.y);
    setCropOffset(clampOffset(rawOx, rawOy, cropZoom));
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  const handleZoomChange = (newZoom: number) => {
    setCropZoom(newZoom);
    setCropOffset(prev => clampOffset(prev.x, prev.y, newZoom));
  };

  // Crop-aware compress: renders the positioned/zoomed image to a 256×256 canvas
  const cropAndCompress = (file: File, offset: { x: number; y: number }, zoom: number): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = (ev) => {
        const src = ev.target?.result as string;
        if (!src) { reject(new Error('empty')); return; }
        const img = new Image();
        img.src = src;
        img.decode()
          .catch(() => {})
          .finally(() => {
            requestAnimationFrame(() => {
              const OUT = 256;
              const nw = img.naturalWidth, nh = img.naturalHeight;
              if (!nw || !nh) { resolve(src); return; }

              // Replicate object-fit:cover for the PREVIEW_SIZE circle
              const coverScale = Math.max(PREVIEW_SIZE / nw, PREVIEW_SIZE / nh);

              // Scale factor from preview pixels → output pixels
              const sf = OUT / PREVIEW_SIZE;

              const dw = nw * coverScale * zoom * sf;
              const dh = nh * coverScale * zoom * sf;
              // Image center in preview: (PREVIEW/2 + offset.x, PREVIEW/2 + offset.y)
              const dx = (PREVIEW_SIZE / 2 + offset.x) * sf - dw / 2;
              const dy = (PREVIEW_SIZE / 2 + offset.y) * sf - dh / 2;

              const canvas = document.createElement('canvas');
              canvas.width = OUT;
              canvas.height = OUT;
              const ctx = canvas.getContext('2d');
              if (!ctx) { resolve(src); return; }
              ctx.clearRect(0, 0, OUT, OUT);
              ctx.drawImage(img, dx, dy, dw, dh);

              const dataUrl = canvas.toDataURL('image/png');
              resolve(dataUrl.length < 200 ? src : dataUrl);
            });
          });
      };
      reader.readAsDataURL(file);
    });

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    pendingPhotoFile.current = file;
    // Show preview via a blob URL — stored in its own state so nothing can reset it
    setPhotoPreview(URL.createObjectURL(file));
    // Reset crop state for the new image
    setCropOffset({ x: 0, y: 0 });
    setCropZoom(1);
    naturalDimsRef.current = null;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSchemaWarning(false);

    // Validation - Only name is required as per request
    if (!formData.name?.trim()) {
        setError("Player name is required.");
        return;
    }

    setIsSaving(true);

    try {
        // If a new photo was selected, crop+compress it now
        let savedPhotoUrl: string | null = formData.photoUrl || null;
        if (pendingPhotoFile.current) {
          try { savedPhotoUrl = await cropAndCompress(pendingPhotoFile.current, cropOffset, cropZoom); } catch {}
          pendingPhotoFile.current = null;
        }

        // Clean payload to ensure empty strings become null for optional text fields
        const clean = (val: string | undefined | null) => (val && val.trim() !== '') ? val.trim() : null;
        const cleanNum = (val: number | undefined | null) => (val === undefined || val === null || isNaN(val)) ? null : val;

        // Helper to validate UUID
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const getValidTeamId = (tid: string | undefined) => {
            if (!tid || !uuidRegex.test(tid)) return null;
            return tid;
        };

        const positionToSave = formData.position || 'Unknown';
        const teamIdToSave = getValidTeamId(formData.teamId);

        // Convert height/weight form inputs → canonical DB units
        const savedHeightCm = parseHeightToCm(
          unitSystem,
          unitSystem === 'imperial'
            ? { feet: heightFt, inches: heightIn }
            : { cm: heightCmInput }
        );
        const savedWeightKg = parseWeightToKg(unitSystem, weightInput);

        const fullPayload = {
          name: formData.name.trim(),
          jersey_number: clean(formData.jerseyNumber),
          position: positionToSave,
          photo_url: savedPhotoUrl,
          team_id: teamIdToSave,
          goals: clean(formData.goals),
          notes: clean(formData.notes),
          // New extended fields
          grad_year: clean(formData.gradYear),
          height_cm: savedHeightCm,    // canonical integer (cm)
          weight_kg: savedWeightKg,    // canonical decimal (kg)
          age: cleanNum(formData.age),
          handedness: clean(formData.handedness),
          primary_serve_type: formData.primaryServeType || null,
          secondary_position: formData.secondaryPosition || null,
        };

        // Determine if this is a temporary/guest ID that shouldn't be saved to DB
        const isUuid = uuidRegex.test(player.id);
        const isTemp = !isUuid || player.id.startsWith('opp-') || player.id.startsWith('dev-') || player.id.startsWith('temp-') || player.id.startsWith('new-');

        if (organizationId === 'dev-org-id' || isTemp) {
            // Local update only
            const updatedPlayer = { ...player, ...formData, photoUrl: savedPhotoUrl ?? formData.photoUrl, position: positionToSave } as Player;

            // If it's a new player in actual DB mode, we still need to perform the insert
            if (isTemp && organizationId !== 'dev-org-id') {
                const { data, error: insertError } = await supabase
                    .from('players')
                    .insert([{
                        ...fullPayload,
                        organization_id: organizationId,
                    }])
                    .select()
                    .single();

                if (insertError) throw insertError;
                onUpdate(mapPlayerFromDB(data));
            } else {
                onUpdate(updatedPlayer);
            }
            onClose();
            return;
        }

        // Try Full Update first for existing players
        let result = await supabase.from('players').update(fullPayload).eq('id', player.id).select();

        if (result.error) {
            const errMsg = result.error.message;
            if (errMsg.includes("Could not find the") && errMsg.includes("column")) {
                console.warn("Schema mismatch detected (missing columns). Retrying with core fields only.");

                const corePayload = {
                    name: formData.name.trim(),
                    jersey_number: clean(formData.jerseyNumber),
                    position: positionToSave,
                    photo_url: savedPhotoUrl,
                    goals: clean(formData.goals),
                    notes: clean(formData.notes)
                };

                result = await supabase.from('players').update(corePayload).eq('id', player.id).select();

                if (!result.error) {
                    setSchemaWarning(true);
                }
            }
        }

        if (result.error) throw result.error;

        if (result.data && result.data.length > 0) {
            const updated = mapPlayerFromDB(result.data[0]);
            const final = schemaWarning ? { ...updated, ...formData } : updated;
            onUpdate(final);
            onClose();
        } else {
            onUpdate({ ...player, ...formData, position: positionToSave } as Player);
            onClose();
        }
    } catch (error: any) {
        console.error("Error saving player", error);
        setError(error.message || "Failed to save player changes. Please try again.");
    } finally {
        setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl p-0 w-full max-w-lg max-h-[90vh] flex flex-col animate-fade-in-up overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-slate-100">
            <h3 className="font-bold text-lg text-slate-900">Edit Player Details</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-50 transition-colors"><X className="w-5 h-5"/></button>
        </div>

        <div className="overflow-y-auto p-6 flex-1">
            <form id="player-edit-form" onSubmit={handleSave} className="space-y-6">

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="flex flex-col items-center space-y-3">
                  {photoPreview ? (
                    /* ── Crop / position UI (new upload only) ── */
                    <>
                      <p className="text-xs text-slate-500 font-medium">Drag to reposition · use slider to zoom</p>
                      <div
                        className="relative rounded-full overflow-hidden border-2 border-blue-400 select-none"
                        style={{
                          width: PREVIEW_SIZE,
                          height: PREVIEW_SIZE,
                          cursor: isDragging ? 'grabbing' : 'grab',
                        }}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerLeave={handlePointerUp}
                      >
                        <img
                          ref={cropImgRef}
                          src={photoPreview}
                          alt="Player"
                          draggable={false}
                          onLoad={() => {
                            if (cropImgRef.current) {
                              naturalDimsRef.current = {
                                w: cropImgRef.current.naturalWidth,
                                h: cropImgRef.current.naturalHeight,
                              };
                            }
                          }}
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transformOrigin: 'center center',
                            transform: `scale(${cropZoom}) translate(${cropOffset.x / cropZoom}px, ${cropOffset.y / cropZoom}px)`,
                            pointerEvents: 'none',
                            userSelect: 'none',
                          }}
                        />
                      </div>

                      {/* Zoom slider */}
                      <div className="flex items-center gap-2" style={{ width: PREVIEW_SIZE }}>
                        <span className="text-[10px] font-bold text-slate-400 w-8">Zoom</span>
                        <input
                          type="range"
                          min={1}
                          max={10}
                          step={0.05}
                          value={cropZoom}
                          onChange={e => handleZoomChange(parseFloat(e.target.value))}
                          className="flex-1 accent-blue-600"
                        />
                        <span className="text-[10px] font-bold text-slate-400 w-8 text-right">{cropZoom.toFixed(1)}×</span>
                      </div>

                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-xs text-blue-600 font-bold hover:underline"
                      >
                        Change Photo
                      </button>
                    </>
                  ) : (
                    /* ── Standard avatar (no new upload) ── */
                    <>
                      <div
                        className="w-24 h-24 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden relative group cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {formData.photoUrl
                          ? <img src={formData.photoUrl} alt="Player" className="w-full h-full object-cover" />
                          : <User className="w-10 h-10 text-slate-400" />
                        }
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Camera className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-xs text-blue-600 font-bold hover:underline"
                      >
                        Change Photo
                      </button>
                    </>
                  )}

                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handlePhotoUpload} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Name <span className="text-red-500">*</span></label>
                        <input
                            required
                            className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-slate-900"
                            style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                            value={formData.name || ''}
                            onChange={e => setFormData({...formData, name: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Jersey #</label>
                        <input
                            className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-slate-900"
                            style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                            value={formData.jerseyNumber || ''}
                            onChange={e => setFormData({...formData, jerseyNumber: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Position</label>
                        <select
                            className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-slate-900"
                            style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                            value={formData.position || 'Unknown'}
                            onChange={e => setFormData({...formData, position: e.target.value})}
                        >
                            {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Also plays <span className="normal-case font-normal text-slate-400">(optional)</span></label>
                        <select
                            className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-slate-900"
                            style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                            value={formData.secondaryPosition || ''}
                            onChange={e => setFormData({...formData, secondaryPosition: e.target.value || undefined})}
                        >
                            <option value="">—</option>
                            {POSITIONS.filter(p => p !== formData.position && p !== 'Unknown').map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Primary Serve Type <span className="normal-case font-normal text-slate-400">(optional)</span></label>
                        <div className="flex rounded-lg border border-slate-300 overflow-hidden">
                            {(['Float', 'Topspin'] as const).map(type => (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, primaryServeType: formData.primaryServeType === type ? undefined : type })}
                                    className={`flex-1 py-2 text-sm font-bold transition-colors ${
                                        formData.primaryServeType === type
                                            ? 'bg-slate-700 text-white'
                                            : 'bg-white text-slate-500 hover:bg-slate-50'
                                    }`}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div>
                    {teams.length > 0 && (
                    <div className="col-span-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Team</label>
                        <select
                            className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-slate-900"
                            style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                            value={formData.teamId || 'unassigned'}
                            onChange={e => setFormData({...formData, teamId: e.target.value})}
                        >
                            <option value="unassigned">Unassigned</option>
                            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </div>
                    )}
                </div>

                {/* Sections */}
                <div className="space-y-3">
                    {/* Details */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <button type="button" onClick={() => toggleSection('details')} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100">
                            <span className="font-bold text-slate-700 text-xs flex items-center gap-2"><Ruler className="w-3 h-3" /> Physical & Athletic</span>
                            {expandedSections.details ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                        {expandedSections.details && (
                            <div className="p-3 grid grid-cols-2 gap-3 bg-white">
                                {/* Height — conditional on unit system */}
                                {unitSystem === 'imperial' ? (
                                    <div className="col-span-2 grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Height — Feet</label>
                                            <input
                                                type="number"
                                                min={3} max={8}
                                                className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900"
                                                style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                                                value={heightFt}
                                                onChange={e => setHeightFt(e.target.value)}
                                                placeholder="5"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Inches</label>
                                            <input
                                                type="number"
                                                min={0} max={11}
                                                className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900"
                                                style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                                                value={heightIn}
                                                onChange={e => setHeightIn(e.target.value)}
                                                placeholder="10"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Height (cm)</label>
                                        <input
                                            type="number"
                                            min={100} max={250}
                                            className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900"
                                            style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                                            value={heightCmInput}
                                            onChange={e => setHeightCmInput(e.target.value)}
                                            placeholder="178"
                                        />
                                    </div>
                                )}
                                {/* Weight */}
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">
                                        Weight ({unitSystem === 'imperial' ? 'lbs' : 'kg'})
                                    </label>
                                    <input
                                        type="number"
                                        min={50} max={400}
                                        className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900"
                                        style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                                        value={weightInput}
                                        onChange={e => setWeightInput(e.target.value)}
                                        placeholder={unitSystem === 'imperial' ? '145' : '66'}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Grad Year</label>
                                    <input
                                        className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900"
                                        style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                                        value={formData.gradYear || ''}
                                        onChange={e => setFormData({...formData, gradYear: e.target.value})}
                                        placeholder="2025"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Handedness</label>
                                    <select
                                        className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900"
                                        style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                                        value={formData.handedness || ''}
                                        onChange={e => setFormData({...formData, handedness: e.target.value as any})}
                                    >
                                        <option value="">-</option>
                                        <option value="Right">Right</option>
                                        <option value="Left">Left</option>
                                        <option value="Ambidextrous">Ambidextrous</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Notes — coaches only */}
                    {isCoach && (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <button type="button" onClick={() => toggleSection('notes')} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100">
                            <span className="font-bold text-slate-700 text-xs flex items-center gap-2"><Target className="w-3 h-3" /> Coach Notes</span>
                            {expandedSections.notes ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                        {expandedSections.notes && (
                            <div className="p-3 bg-white">
                                <textarea
                                    className="w-full p-2 border border-slate-300 rounded-lg text-sm h-20 bg-white text-slate-900"
                                    style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                                    value={formData.notes || ''}
                                    onChange={e => setFormData({...formData, notes: e.target.value})}
                                    placeholder="Coach notes..."
                                />
                            </div>
                        )}
                    </div>
                    )}
                </div>
            </form>
        </div>

        <div className="p-4 border-t border-slate-100 flex gap-3">
            <button onClick={onClose} className="flex-1 py-2 text-slate-600 font-bold hover:bg-slate-50 rounded-lg transition-colors">Cancel</button>
            <button form="player-edit-form" type="submit" disabled={isSaving} className="flex-1 py-2 bg-blue-600 text-white font-bold hover:bg-blue-700 rounded-lg shadow-sm transition-colors flex items-center justify-center gap-2">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
            </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerEditModal;
